/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import InputAdornment from "@mui/material/InputAdornment";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { AlertIcon, colors } from "@dotnet/telematics-design-system";

import { muiOutlinedInputDefaultStylesOverrides } from "../../shared";
import InfoIconWithTooltip from "../IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import Label from "../Label/Label";
import OptionalTooltip from "../Tooltip/OptionalTooltip";
import {
  inputPropsStyleOverrides,
  textFieldStyleOverrides
} from "./styleConstants";
import { useStyles } from "./styles";
import { InputFieldProps } from "./types";

const InputField = ({
  size,
  labelLeft,
  labelIcon,
  labelRight,
  iconLeft,
  iconRight,
  readOnly,
  errorText,
  customStyle,
  customInputStyle,
  textRef,
  testId,
  tooltipText,
  labelAddition,
  inputStyleExceptions,
  infoTooltip,
  ...props
}: InputFieldProps & Omit<TextFieldProps, "size">) => {
  const { errorIcon } = useStyles();

  const paddings = {
    tiny: "2px 12px",
    small: "8px 12px",
    medium: "12px"
  };

  const padding = paddings[size];

  const repeatedStyles = muiOutlinedInputDefaultStylesOverrides(colors);
  const customInputStyleExists = customInputStyle
    ? { input: customInputStyle }
    : {};

  return (
    <div
      css={css({
        display: "inline-flex",
        flexDirection: "column",
        width: props.fullWidth ? "100%" : "unset",
        "& .Mui-disabled": {
          backgroundColor: colors.gray100
        },
        ...customStyle
      })}
    >
      {(labelLeft || labelRight) && (
        <div css={css({ display: "flex" })}>
          <Label
            titleLeft={labelLeft}
            titleRight={labelRight}
            titleTooltip={labelIcon}
            isRequired={props.required}
          />

          {labelAddition && (
            <>
              <Typography variant="body2Medium">&nbsp;</Typography>
              <Typography
                variant="body2Medium"
                sx={{
                  color: colors.textPlaceholder
                }}
              >
                {labelAddition}
              </Typography>
            </>
          )}

          {infoTooltip && (
            <>
              <Typography variant="body2Medium">&nbsp;</Typography>
              <InfoIconWithTooltip
                title={infoTooltip}
                iconTestId={`${infoTooltip}-info icon`}
                customIconStyle={{
                  display: "inline-grid",
                  height: "22px",
                  alignContent: "center"
                }}
                tooltipSize="medium"
              />
            </>
          )}
        </div>
      )}

      <OptionalTooltip
        tooltipPlacement="top"
        tooltipTitle={tooltipText ?? ""}
        isDisabled={!tooltipText}
      >
        <TextField
          key={`${props.disabled}`}
          data-testid={testId}
          inputRef={textRef}
          {...props}
          error={Boolean(errorText)}
          autoComplete="off"
          helperText={
            errorText ? (
              <Typography
                variant="body2Medium"
                css={css({
                  display: "flex"
                })}
              >
                <AlertIcon data-testid="error-icon" css={css(errorIcon)} />
                {errorText}
              </Typography>
            ) : (
              ""
            )
          }
          slotProps={{
            input: {
              ...(props.InputProps || props.slotProps?.input),
              spellCheck: "false",
              readOnly,
              ...(iconLeft && {
                startAdornment: (
                  <InputAdornment position="start">{iconLeft}</InputAdornment>
                )
              }),
              ...(iconRight && {
                endAdornment: (
                  <InputAdornment position="end">{iconRight}</InputAdornment>
                )
              }),
              sx: [
                {
                  background:
                    props.disabled || readOnly ? colors.gray100 : colors.white,
                  padding
                },
                inputStyleExceptions as {},
                inputPropsStyleOverrides,
                repeatedStyles,
                customInputStyleExists
              ]
            }
          }}
          sx={[
            textFieldStyleOverrides,
            {
              "& .Mui-disabled .MuiButtonBase-root": {
                backgroundColor: colors.gray100,
                color: colors.textPlaceholder
              }
            }
          ]}
        />
      </OptionalTooltip>
    </div>
  );
};

export default InputField;
